import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import DogwoodLogo from '../ui/DogwoodLogo';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { formatApiErrorDetail } from '../../lib/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.first_name || 'Member'}!`);
      navigate('/member-dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-32 flex items-center justify-center bg-[#f1e3eb]">
      <div className="w-full max-w-md px-4 py-12">
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#bd8cae] to-[#bd8cae] flex items-center justify-center mx-auto mb-4">
              <DogwoodLogo size={42} className="text-white" />
            </div>
            <CardTitle className="font-serif text-2xl text-[#3c4a2a]">
              Member Login
            </CardTitle>
            <CardDescription className="text-[#3c4a2a]/60">
              Sign in to access your member portal
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[#3c4a2a]">
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-[#3c4a2a]">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#5d6b3c] hover:text-[#3c4a2a]"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3c4a2a]/40 hover:text-[#3c4a2a]"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-6"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-[#3c4a2a]/60 text-sm">
                Not a member yet?{' '}
                <Link
                  to="/register"
                  className="text-[#5d6b3c] hover:text-[#3c4a2a] font-medium"
                >
                  Create an account
                </Link>
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-[#bd8cae]/30">
              <p className="text-center text-xs text-[#3c4a2a]/50">
                By signing in, you agree to our{' '}
                <Link to="/terms" className="text-[#5d6b3c]">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-[#5d6b3c]">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default LoginPage;
